import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { useViewDataImmediate } from "@/features/views/hooks/useViewData"
import { ViewShell } from "@/features/views/components/ViewShell"
import { EveningSection } from "@/features/views/components/EveningSection"
import { VIEW_REGISTRY } from "@/features/views/viewRegistry"
import { ExternalTodayCalendar, useCalendarRefresh } from "@/features/calendar"
import { useSettingBoolean } from "@/features/settings/hooks/useSetting"
import type { ViewHeaderAction } from "@/features/views/components/ViewHeader"
import LayoutIcon from "@/shared/icons/pikaicons/stroke/general/layout-grid-stack-left.svg?react"

/**
 * Today page — primary view showing today's tasks.
 * Uses useViewDataImmediate for instant render with placeholder data.
 * Includes an evening subsection at the bottom (matching v1's TotalLater).
 */
export function TodayPage() {
  useCalendarRefresh()
  const { t } = useTranslation()
  const [todayData, setTodayOptimistic, todayLoading] =
    useViewDataImmediate("today")
  const [eveningData, , eveningLoading] = useViewDataImmediate("evening")
  const reg = VIEW_REGISTRY.today
  const isLoading = todayLoading || eveningLoading

  const { value: isFlatToday, toggle: toggleFlat } =
    useSettingBoolean("#flat-today")

  const actions: ViewHeaderAction[] = useMemo(
    () => [
      {
        label: isFlatToday
          ? t("Group by Project")
          : t("Don't Group"),
        icon: <LayoutIcon className="size-4" />,
        onClick: toggleFlat,
      },
    ],
    [isFlatToday, toggleFlat, t],
  )

  return (
    <div>
      <ViewShell
        title={reg.label}
        icon={reg.icon}
        items={todayData?.items ?? []}
        emptyState={reg.emptyState}
        setOptimistic={setTodayOptimistic}
        isLoading={isLoading}
        actions={actions}
        beforeContent={
          <div className="mt-10 mb-[30px]">
            <ExternalTodayCalendar />
          </div>
        }
      />

      {/* Evening subsection — auto-hides when empty */}
      <div className="mx-auto max-w-[900px]">
        <EveningSection items={eveningData?.items ?? []} />
      </div>
    </div>
  )
}
